// CouncilHistory.jsx — history sidebar + responsive drawer for Council v2.
//
// Desktop: a persistent sidebar column (rendered by CouncilView as a sibling).
// Mobile: the same list inside MobileDrawer, opened from a header button.
//
// Data comes from useCouncilHistory (cursor paginated, user-scoped server-side).
// Opening an item fetches the detail (normalized) and offers reuse, which hands
// a payload back to the composer.

import { Loader2, RefreshCw, History as HistoryIcon, RotateCcw } from 'lucide-react';
import { historyItemSummary } from './councilModel.js';
import { statusLabel } from '../../lib/councilProtocol.js';

function StatusPill({ status }) {
  const tone = status === 'complete' ? 'text-apolo-success'
    : status === 'partial' ? 'text-apolo-warning'
    : status === 'cancelled' ? 'text-apolo-textTertiary'
    : 'text-apolo-error';
  return <span className={`text-[9.5px] font-semibold uppercase tracking-wide ${tone}`}>{statusLabel(status)}</span>;
}

export function CouncilHistoryList({ history, onReuse, onOpenDetail, activeId }) {
  const {
    items, loading, loadingMore, error, hasMore,
    refresh, loadMore, openDeliberation,
  } = history;

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center justify-between gap-2 px-3 py-2">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-apolo-textTertiary">
          <HistoryIcon size={12} aria-hidden="true" />
          Histórico
        </span>
        <button type="button" onClick={refresh} disabled={loading} aria-label="Atualizar histórico" className="inline-flex h-6 w-6 items-center justify-center rounded text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary disabled:opacity-40">
          <RefreshCw size={11} aria-hidden="true" className={loading ? 'animate-spin' : ''} />
        </button>
      </header>

      {error && <p role="alert" className="mx-3 mb-2 rounded border border-apolo-error/30 bg-apolo-errorBg px-2 py-1 text-[10.5px] text-apolo-error">{error}</p>}

      <div className="flex-1 overflow-y-auto px-2 pb-2">
        {loading && items.length === 0 ? (
          <p className="flex items-center gap-1.5 px-2 py-4 text-[11px] text-apolo-textTertiary"><Loader2 size={11} className="animate-spin" aria-hidden="true" /> Carregando…</p>
        ) : items.length === 0 ? (
          <p className="px-2 py-4 text-[11px] text-apolo-textTertiary">Nenhuma deliberação salva ainda.</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {items.map((item) => {
              const { title, subtitle } = historyItemSummary(item);
              return (
                <li key={item.id}>
                  <div className={`rounded-lg border px-2.5 py-2 transition ${activeId === item.id ? 'border-apolo-accent/50 bg-apolo-surface2' : 'border-transparent hover:border-apolo-borderSubtle hover:bg-apolo-surface2/60'}`}>
                    <button
                      type="button"
                      onClick={() => { openDeliberation(item.id); onOpenDetail?.(item.id); }}
                      className="block w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing"
                    >
                      <span className="block truncate text-[11.5px] font-medium text-apolo-textPrimary">{title}</span>
                      <span className="mt-0.5 flex items-center gap-1.5">
                        <StatusPill status={item.status} />
                        <span className="truncate text-[9.5px] text-apolo-textTertiary">{subtitle}</span>
                      </span>
                    </button>
                    <div className="mt-1.5 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => onReuse?.(item.id)}
                        className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-apolo-textTertiary transition hover:bg-apolo-surface3 hover:text-apolo-textPrimary"
                      >
                        <RotateCcw size={10} aria-hidden="true" /> Reutilizar
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {hasMore && (
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            className="mt-2 w-full rounded-md border border-apolo-borderSubtle bg-apolo-surface2 py-1.5 text-[11px] text-apolo-textSecondary transition hover:text-apolo-textPrimary disabled:opacity-50"
          >
            {loadingMore ? 'Carregando…' : 'Carregar mais'}
          </button>
        )}
      </div>
    </div>
  );
}
